import { Model } from 'app/model/backendmodel';
import { NamedAsyncAdapter } from './named-async.adapter';
import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from './adapter-registry';
import { getString } from 'app/shared/utils/string.util';
import { Factory } from '../factory/factory';
import { GlobalParameter } from '../factory/global-parameter';
import { IStringMap } from 'app/interfaces/IStringMap';
import { GlobalVariables } from 'app/services/globals';
import {
    FactoryStagingPropConstants,
    LinkedService,
    linkedServiceReferenceToResource,
    ReferenceAdapter
} from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Factory),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            globalParameters: '*',
            globalConfigurations: '*',
            encryption: '*',
            trustModeClaimForMi: '*',
        },
        identity: '*'
    }
})
export class FactoryAsyncAdapter extends NamedAsyncAdapter<Factory, Model.Factory> {
    public readonly isFactoryStagingSettingEnabled: boolean = true;
    protected async _toResource(factory: Factory): Promise<Model.Factory> {
        const factoryResource = (await super._toResource(factory)) as Model.Factory;
        const parameters: { [propertyName: string]: Model.GlobalParameterSpecification } = {};
        const parameterAdapter = this.context.adapterFor(GlobalParameter.MODEL);
        if (parameterAdapter) {
            factory.parameters.forEach((parameter) => {
                if (parameter.name) {
                    parameters[getString(parameter.name)] = parameterAdapter.toResource(parameter);
                }
            });
        }
        this.properties(factoryResource).globalParameters = factory.parameters.size > 0 ? parameters : undefined;

        if (factory.encryption) {
            this.properties(factoryResource).encryption = factory.encryption;
        }

        if (this.isFactoryStagingSettingEnabled && !!factory.stagingLinkedService) {
            const linkedService = linkedServiceReferenceToResource(
                factory.stagingLinkedService,
                this.context,
                undefined,
                this.ensureLogger()
            );
            factory.configurations[FactoryStagingPropConstants.stagingLSReferenceName] = linkedService.referenceName;
            factory.configurations[FactoryStagingPropConstants.stagingLSType] = linkedService.type;
        }

        if (
            (GlobalVariables.enableDataFlowVersion || GlobalVariables.enablePipelineBilling) &&
            factory.configurations
        ) {
            const globalConfigs: IStringMap<string> = {};
            Object.keys(factory.configurations).forEach((config) => {
                globalConfigs[config] = factory.configurations[config];
            });
            this.properties(factoryResource).globalConfigurations =
                Object.keys(factory.configurations).length > 0 ? globalConfigs : undefined;
        }

        if (factory.trustModeClaimForMi != null) {
            this.properties(factoryResource).trustModeClaimForMi = factory.trustModeClaimForMi;
        }

        factoryResource.location = factory.location;
        factoryResource.identity = factory.identity;
        return factoryResource;
    }

    protected async _toModel(modelFactory: Model.Factory, factory?: Factory): Promise<Factory> {
        if (!factory) {
            factory = Factory.MODEL.create<Factory>();
        }
        await super._toModel(modelFactory, factory);
        factory.location = factory.location || modelFactory.location;
        factory.identity = factory.identity || modelFactory.identity;

        if (modelFactory.properties?.encryption) {
            factory.encryption = modelFactory.properties.encryption;
        }

        const parameterAdapter = this.context.adapterFor(GlobalParameter.MODEL);
        if (parameterAdapter) {
            if (modelFactory.properties?.globalParameters) {
                for (const parameterName in modelFactory.properties.globalParameters) {
                    factory.parameters.add(
                        parameterAdapter.toModel(
                            modelFactory.properties.globalParameters[parameterName],
                            undefined,
                            parameterName,
                            Factory.MODEL.name,
                            factory.name
                        )
                    );
                }
            }
        }
        if (
            (GlobalVariables.enableDataFlowVersion || GlobalVariables.enablePipelineBilling) &&
            modelFactory.properties &&
            modelFactory.properties.globalConfigurations
        ) {
            for (const globalConfig in modelFactory.properties.globalConfigurations) {
                factory.configurations[globalConfig] = modelFactory.properties.globalConfigurations[globalConfig];
            }
        }
        if (
            this.isFactoryStagingSettingEnabled &&
            !!factory.configurations[FactoryStagingPropConstants.stagingLSReferenceName]
        ) {
            const linkedServiceReference: Model.LinkedServiceReference = {
                referenceName: factory.configurations[FactoryStagingPropConstants.stagingLSReferenceName],
                type: factory.configurations[FactoryStagingPropConstants.stagingLSType]
            };
            factory.stagingLinkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                Metamodel.modelFor(LinkedService),
                linkedServiceReference, this.context
            );
        }
        if (modelFactory.properties?.trustModeClaimForMi) {
            factory.trustModeClaimForMi = modelFactory.properties.trustModeClaimForMi;
        }

        return factory;
    }
}
